import React, { useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import EditIcon from '@mui/icons-material/Edit';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Alert, Box, Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material';

import { property as propertyGlobals } from '@luxuryescapes/lib-global';

import Image from '~/components/Common/Image';
import PropertyMap from '~/components/Common/PropertyMap';
import DebugModal from '~/components/DebugModal/DebugModal';
import PropertyUploadData from '~/components/Vendors/Properties/PropertyUploadData';
import formatGeoData from '~/components/Vendors/helpers/formatGeoData';

import { PROPERTY_CHANNEL_MANAGERS } from '~/consts/reservation';

import { formatDateLongMonthWithMeridiemNoSeconds } from '~/services/TimeService';

export type OfferBuilderProperty = App.Property & {
  address?: string;
  ari_last_updated_at?: string;
  category?: string;
  channel_managed?: boolean;
  children_policy?: string;
  exclude_flash_pay_at_property?: boolean;
  generate_infant_capacities_from_children?: boolean;
  hotel_meta_code?: string;
  hotel_meta_provider?: string;
  logo_id_cloudinary_external?: string;
  location_heading?: string;
  location_subheading?: string;
  margin_sharing_enabled?: boolean;
  max_child_age?: number;
  max_infant_age?: number;
  max_teenager_age?: number | null;
  send_nightly_reservation_rates?: boolean;
  taxes_and_fees_value?: string;
  taxes_payable_at_property?: boolean;
  type?: string;
};

interface OfferBuilderPropertyInformationProps {
  property?: OfferBuilderProperty;
  vendorId?: string;
  isLoadingProperty: boolean;
  offerError: Error | null;
  propertyError: Error | null;
}

function formatValue(value?: string | number | null) {
  return value === null || value === undefined || value === '' ? '-' : value;
}

function getPropertyTypeName(type?: string) {
  if (type === propertyGlobals.UNIQUE_STAYS_TYPE) {
    return 'Unique Stays';
  }

  if (type === propertyGlobals.RENTAL_TYPE) {
    return 'Rental';
  }

  return 'Hotel';
}

export function formatPropertyLocation(property?: OfferBuilderProperty) {
  const city = property?.location_heading;
  const country = property?.geo_data?.country;

  return [city, country].filter(Boolean).join(' · ') || '-';
}

export default function OfferBuilderPropertyInformation({
  property,
  vendorId,
  isLoadingProperty,
  offerError,
  propertyError,
}: OfferBuilderPropertyInformationProps) {
  return (
    <Box>
      <SectionHeading
        title="Property information"
        subtitle="Verify the core property record. Coordinates must match the map pin and address."
        actions={
          <>
            <DebugModal type="property" title="Debug" data={property || {}} />
            {vendorId && property?.id ? (
              <Button
                component={Link}
                to={`/vendors/${vendorId}/edit-property/${property.id}`}
                variant="contained"
                startIcon={<EditIcon />}
              >
                Edit
              </Button>
            ) : (
              <Button variant="contained" startIcon={<EditIcon />} disabled>
                Edit
              </Button>
            )}
          </>
        }
      />

      {isLoadingProperty && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading property data...
        </Alert>
      )}

      {offerError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load offer data: {offerError.message}
        </Alert>
      )}

      {propertyError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load property data: {propertyError.message}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 280px', xl: 'minmax(0, 1fr) 320px' },
          gap: { xs: 2, xl: 3 },
          alignItems: 'start',
        }}
      >
        <Paper variant="outlined">
          <Box sx={{ p: 2.5 }}>
            <Typography variant="h6">Property overview</Typography>
          </Box>
          <Divider />
          <Box sx={{ p: 2.5 }}>
            <PropertyOverviewRow label="Hotel code" value={property?.hotel_code || '-'} />
            <PropertyOverviewRow label="Address" value={property?.address || '-'} />
            <PropertyOverviewRow label="Type" value={getPropertyTypeName(property?.type)} />
            <PropertyOverviewRow label="Category" value={property?.category || '-'} />
            <PropertyOverviewRow label="Location city" value={property?.location_heading || '-'} />
            <PropertyOverviewRow label="Location country/state" value={property?.location_subheading || '-'} />
            <PropertyOverviewRow label="Children policy" value={property?.children_policy || '-'} />
            <PropertyOverviewRow label="Max child age" value={formatValue(property?.max_child_age)} />
            <PropertyOverviewRow label="Max infant age" value={formatValue(property?.max_infant_age)} />
            <PropertyOverviewRow
              label="Max teenager age"
              value={property?.max_teenager_age != null ? formatValue(property.max_teenager_age) : 'Not enabled'}
            />
            <PropertyOverviewRow label="Taxes and fees" value={property?.taxes_and_fees_value || '-'} />
            <PropertyOverviewRow label="Geolocation data" value={formatGeoData(property?.geo_data, 'property')} />
            {property?.taxes_payable_at_property && (
              <PropertyOverviewRow label="Has taxes and fees payable at property" value="Yes" />
            )}
            {property?.channel_manager === PROPERTY_CHANNEL_MANAGERS.derbysoft && (
              <>
                <PropertyOverviewRow
                  label="Send nightly reservation rates"
                  value={property.send_nightly_reservation_rates ? 'Yes' : 'No'}
                />
                <PropertyOverviewRow
                  label="Generate infant capacities from children"
                  value={property.generate_infant_capacities_from_children ? 'Yes' : 'No'}
                />
              </>
            )}
            {property?.exclude_flash_pay_at_property && (
              <PropertyOverviewRow label="Exclude Pay @ Property for LTLE" value="Yes" />
            )}
            {property && property.channel_manager !== PROPERTY_CHANNEL_MANAGERS.selfManaged && (
              <PropertyOverviewRow
                label="LPC auto LuxPlus margin sharing enabled"
                value={property.margin_sharing_enabled !== false ? 'Yes' : 'No'}
              />
            )}
          </Box>
        </Paper>

        <Stack spacing={2}>
          {property?.channel_manager && (
            <InfoCard title="Channel manager">
              <Chip label={property.channel_manager} color="info" size="small" sx={{ alignSelf: 'flex-start' }} />
            </InfoCard>
          )}

          {property?.channel_managed && (
            <InfoCard title="Last ARI update">
              <Typography variant="body2">
                {property.ari_last_updated_at
                  ? formatDateLongMonthWithMeridiemNoSeconds(property.ari_last_updated_at)
                  : 'Never'}
              </Typography>
            </InfoCard>
          )}

          {property?.logo_id_cloudinary_external && (
            <InfoCard title="Logo">
              <Box
                sx={{
                  display: 'grid',
                  minHeight: 96,
                  placeItems: 'center',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  bgcolor: 'background.default',
                  '& img': {
                    maxHeight: 96,
                    objectFit: 'contain',
                  },
                }}
              >
                <Image className="img-responsive" publicId={property.logo_id_cloudinary_external} />
              </Box>
            </InfoCard>
          )}

          <InfoCard title="Property meta">
            {property?.hotel_meta_provider ? (
              <Stack spacing={0.5}>
                <Typography variant="body2">Provider: {property.hotel_meta_provider}</Typography>
                <Typography variant="body2">Code: {property.hotel_meta_code || 'Not provided'}</Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Property not mapped to any meta provider.
              </Typography>
            )}
          </InfoCard>

          <InfoCard title="Property data">
            <PropertyUploadData
              vendorId={vendorId || ''}
              propertyId={property?.id}
              channelManager={property?.channel_manager}
              hideTitle
              buttonProps={{
                variant: 'outlined',
                disabled: !vendorId || !property?.id,
                fullWidth: true,
                startIcon: <UploadFileIcon />,
              }}
            />
          </InfoCard>
        </Stack>
      </Box>

      <LocationMapSection property={property} />
    </Box>
  );
}

function SectionHeading({ title, subtitle, actions }: { title: string; subtitle: string; actions?: React.ReactNode }) {
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
      <Box>
        <Typography variant="h4">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
      {actions && (
        <Stack direction="row" spacing={1}>
          {actions}
        </Stack>
      )}
    </Stack>
  );
}

function PropertyOverviewRow({
  label,
  value,
  action,
}: {
  label: string;
  value: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '180px minmax(0, 1fr) 32px' },
        gap: 2,
        py: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        alignItems: 'center',
        '&:last-of-type': {
          borderBottom: 0,
        },
      }}
    >
      <Typography variant="body2" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
      {action}
    </Box>
  );
}

function LocationMapSection({ property }: { property?: OfferBuilderProperty }) {
  const [mapKey, setMapKey] = useState(0);
  const latitude = property?.latitude;
  const longitude = property?.longitude;
  const hasCoordinates = typeof latitude === 'number' && typeof longitude === 'number';
  const mapsUrl = hasCoordinates
    ? `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
    : undefined;
  const locationMapSubtitle = hasCoordinates
    ? `Lat ${latitude} · Lng ${longitude} · Use Map / Satellite controls inside the frame · pin shows property coordinates`
    : 'Property coordinates shown on map';

  return (
    <Paper variant="outlined" sx={{ mt: 3, overflow: 'hidden' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ p: 2.5 }}>
        <Box>
          <Typography variant="h5">Location map</Typography>
          <Typography variant="body2" color="text.secondary">
            {locationMapSubtitle}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5}>
          <Button
            variant="text"
            size="small"
            startIcon={<MyLocationIcon />}
            disabled={!hasCoordinates}
            onClick={() => setMapKey((currentKey) => currentKey + 1)}
            sx={{ minWidth: 0, px: 1 }}
          >
            Re-center
          </Button>
          <Button
            variant="text"
            size="small"
            startIcon={<OpenInNewIcon />}
            disabled={!mapsUrl}
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ minWidth: 0, px: 1 }}
          >
            Open in Maps
          </Button>
        </Stack>
      </Stack>
      <Divider />
      {hasCoordinates ? (
        <PropertyMap key={mapKey} lat={latitude} lng={longitude} />
      ) : (
        <Box sx={{ height: 360, display: 'grid', placeItems: 'center', bgcolor: 'grey.50' }}>
          <Typography variant="body2" color="text.secondary">
            No property coordinates available.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, overflow: 'hidden' }}>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="overline" color="text.secondary">
            {title}
          </Typography>
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}
